import { useState, useEffect, useRef } from "react";
import { areaParamsTranslate, areaOrStr, areaTextEndKeyword } from "./search";

export const TEMPORARY_ID = 1000000000000;

export const useDidUpdateEffect = (fn, inputs) => {
  const didMountRef = useRef(false);

  useEffect(() => {
    if (didMountRef.current) fn();
    else didMountRef.current = true;
  }, inputs);
};

export const useAreaKeywords = (request, items, vol_or_job) => {
  let areaKeywordsArray = request.area_keywords || [];

  // エリア名の補完 spのみ
  try {
    if (Array.isArray(items.areas)) {
      const areaNames = areaParamsTranslate(
        request.area_ids || request.area || [],
        [...items.areas, items.extended_areas[vol_or_job]]
      ).areaKeywords;
      areaNames.forEach((name) => {
        if (!areaKeywordsArray.includes(name)) {
          areaKeywordsArray = [...areaKeywordsArray, name]; // areaKeywordsArray | areaNames
        }
      });

      if (areaKeywordsArray[0] && !areaNames?.includes(areaKeywordsArray[0])) {
        // テキスト窓から入力された文字列かどうか判定
        areaKeywordsArray[0] += areaTextEndKeyword;
      }
    }
  } catch (e) {}

  return useState(areaKeywordsArray.join(areaOrStr));
};

export const useSearchIds = (props) => {
  const [statusIds, setStatusIds] = useState(
    props.status || props.status_ids || []
  );
  const [areaIds, setAreaIds] = useState(props.area || props.area_ids || []);
  const [genreIds, setGenreIds] = useState(
    props.genre || props.genre_ids || []
  );
  const [kindIds, setKindIds] = useState(props.kind || props.kind_ids || []);
  const [positionIds, setPositionIds] = useState(
    props.position || props.posi_ids || props.posi || []
  );
  const [workingFormIds, setWorkingFormIds] = useState(
    props.working_form || props.wf_ids || props.wf || []
  );
  const [jobTypeIds, setJobTypeIds] = useState(
    props.job_type || props.jt_ids || props.jt || []
  );
  const [tagIds, setTagIds] = useState(props.tag || props.tag_ids || []);
  const [characteristicIds, setCharacteristicIds] = useState(
    props.characteristic || props.chara_ids || props.chara || []
  );
  const [atmosphereIds, setAtmosphereIds] = useState(
    props.atmosphere || props.atm_ids || props.atm || []
  );
  const [universityIds, setUniversityIds] = useState(
    props.university || props.uni_ids || props.uni || []
  );
  const [skillIds, setSkillIds] = useState(
    props.skill || props.skill_ids || []
  );
  const [jobCharacteristicIds, setJobCharacteristicIds] = useState(
    props.job_characteristic || props.j_chara_ids || props.j_chara || []
  );
  const [serviceTypeIds, setServiceTypeIds] = useState(
    props.service_type || props.st_ids || props.st || []
  );
  const [charitableGiftIds, setCharitableGiftIds] = useState(
    props.charitable_gift || props.cg_ids || props.cg || []
  );
  const [priceCategoryIds, setPriceCategoryIds] = useState(
    props.price_category || props.price_ids || props.price || []
  );

  return [
    {
      statusIds,
      areaIds,
      genreIds,
      tagIds,
      characteristicIds,
      atmosphereIds,
      universityIds,
      positionIds,
      skillIds,
      jobTypeIds,
      jobCharacteristicIds,
      workingFormIds,
      serviceTypeIds,
      charitableGiftIds,
      kindIds,
      priceCategoryIds,
    },
    {
      setStatusIds,
      setAreaIds,
      setGenreIds,
      setTagIds,
      setCharacteristicIds,
      setAtmosphereIds,
      setUniversityIds,
      setPositionIds,
      setSkillIds,
      setJobTypeIds,
      setJobCharacteristicIds,
      setWorkingFormIds,
      setServiceTypeIds,
      setCharitableGiftIds,
      setKindIds,
      setPriceCategoryIds,
    },
  ];
};

export const useRangeActivities = (props) => {
  const [termDaysGte, setTermDaysGte] = useState(props.term_days_gte || null);
  const [termDaysLte, setTermDaysLte] = useState(props.term_days_lte || null);
  const [frequentPerMonthGte, setFrequentPerMonthGte] = useState(
    props.frequent_per_month_gte || null
  );
  const [frequentPerMonthLte, setFrequentPerMonthLte] = useState(
    props.frequent_per_month_lte || null
  );

  return [
    {
      termDaysGte,
      termDaysLte,
      frequentPerMonthGte,
      frequentPerMonthLte,
    },
    {
      setTermDaysGte,
      setTermDaysLte,
      setFrequentPerMonthGte,
      setFrequentPerMonthLte,
    },
  ];
};

export const useExtractSelectedTermValue = () => {
  const [activityTermContent, setActivityTermContent] = useState({
    lower_limit: "",
    upper_limit: "",
  });

  const assignSelectedTermLabel = (updateKey, value) => {
    setActivityTermContent((prevState) => ({
      ...prevState,
      [updateKey]: value,
    }));
  };

  return [activityTermContent, assignSelectedTermLabel];
};

export const useSelectedActivityTermContent = (
  state,
  items,
  setActivityTermContent,
  useLabel = true
) => {
  const { activity_term_attributes } = state;
  const { activity_terms } = items;

  useEffect(() => {
    if (activity_term_attributes && activity_term_attributes.length > 0) {
      if (activity_term_attributes[0].id < TEMPORARY_ID) {
        const foundLowerItem = activity_terms.find(
          (item) => item.alias === activity_term_attributes[0].lower_limit
        ) || { value: "", label: "" };
        const foundUpperItem = activity_terms.find(
          (item) => item.alias === activity_term_attributes[0].upper_limit
        ) || { value: "", label: "" };

        if (useLabel) {
          setActivityTermContent("lower_limit", foundLowerItem.label);
          setActivityTermContent("upper_limit", foundUpperItem.label);
        } else {
          setActivityTermContent("lower_limit", foundLowerItem.value);
          setActivityTermContent("upper_limit", foundUpperItem.value);
        }
      }
    }
  }, [activity_term_attributes]);
};

export const useSelectedActivityTermContentForSingly = (
  state,
  items,
  upperOption,
  setActivityTermContent,
  useLabel = true
) => {
  const { activity_term_attributes } = state;
  const { activity_terms } = items;

  useEffect(() => {
    if (activity_term_attributes && activity_term_attributes.length > 0) {
      if (activity_term_attributes[0].id < TEMPORARY_ID) {
        const foundLowerItem = activity_terms.find(
          (item) => item.alias === activity_term_attributes[0].lower_limit
        ) || { value: "", label: "" };
        const foundUpperItem = upperOption.find(
          (item) => item.alias === activity_term_attributes[0].upper_limit
        ) || { value: "", label: "" };

        if (useLabel) {
          setActivityTermContent("lower_limit", foundLowerItem.label);
          setActivityTermContent("upper_limit", foundUpperItem.label);
        } else {
          setActivityTermContent("lower_limit", foundLowerItem.value);
          setActivityTermContent("upper_limit", foundUpperItem.value);
        }
      }
    }
  }, [activity_term_attributes]);
};

export const useSelectedActivityFrequentContent = (
  state,
  items,
  setActivityFrequentContent,
  useLabel = true
) => {
  const { activity_frequent_attributes } = state;
  const { activity_frequents } = items;

  useEffect(() => {
    if (
      activity_frequent_attributes &&
      activity_frequent_attributes.length > 0
    ) {
      if (activity_frequent_attributes[0].id < TEMPORARY_ID) {
        const lower = activity_frequent_attributes[0].lower_limit;
        const upper = activity_frequent_attributes[0].upper_limit;
        let selectedValue;
        if (
          lower === upper &&
          activity_frequent_attributes[0].sub_monthly_type
        ) {
          selectedValue =
            activity_frequents.find(
              (item) =>
                item.value === activity_frequent_attributes[0].sub_monthly_type
            ) || "";
        } else {
          selectedValue =
            activity_frequents.find(
              (item) =>
                item.alias.lower_limit === lower &&
                item.alias.upper_limit === upper
            ) || "";
        }
        if (useLabel) {
          setActivityFrequentContent(selectedValue.label);
        } else {
          setActivityFrequentContent(selectedValue.value);
        }
      }
    }
  }, [activity_frequent_attributes]);
};
